/* eslint-disable react/prop-types */
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ZAxis,
} from "recharts";
import apiUrls from "../../../../api";
import { formatContentTypeName } from "../../../graph/util/NameFormatter";

export interface InteractionStat {
  contentId: number;
  contentName: string;
  contentType: string;
  sceneId: number | null;
  sceneName: string | null;
  attemptCount: number;
  successRate: number;
  avgScore: number;
  scoreMax: number;
  avgDuration: number;
}

interface ScatterTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: InteractionStat }>;
  t: (key: string) => string;
  getSuccessRateColor: (rate: number) => string;
}

interface InteractionAnalysisTableProps {
  projectId: number;
  startDate?: string;
  endDate?: string;
  filterSceneId?: number | string | null;
  filterSceneName?: string | null;
  onClearFilter?: () => void;
  onSceneClick?: (sceneId: number, sceneName: string) => void;
  onInteractionClick?: (contentId: number, contentName: string, contentType: string) => void;
}

const InteractionAnalysisTable = ({
  projectId,
  startDate,
  endDate,
  filterSceneId,
  filterSceneName,
  onClearFilter,
  onSceneClick,
  onInteractionClick,
}: InteractionAnalysisTableProps) => {
  const { t } = useTranslation();
  const [data, setData] = useState<InteractionStat[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortKey, setSortKey] = useState<"successRate" | "avgDuration" | "attemptCount">(
    "successRate",
  );
  const [sortAsc, setSortAsc] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<"table" | "chart">("table");
  const fetchKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const fetchKey = `${projectId}-${startDate || "null"}-${endDate || "null"}`;
      if (fetchKeyRef.current === fetchKey) return;
      fetchKeyRef.current = fetchKey;

      setIsLoading(true);
      try {
        let url = `${apiUrls.stats.interactionStats}?project=${projectId}`;
        if (startDate) url += `&startDate=${startDate}`;
        if (endDate) url += `&endDate=${endDate}`;

        const response = await axios.get(url);
        setData(response.data || []);
      } catch (error) {
        console.error("Error fetching interaction stats:", error);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [projectId, startDate, endDate]);

  const handleSort = (key: "successRate" | "avgDuration" | "attemptCount") => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(key === "successRate"); // Default: success rate asc (worst first), others desc
    }
  };

  // Filter by scene if filterSceneId is provided
  const sceneFilteredData = filterSceneId
    ? data.filter((item) => item.sceneId === Number(filterSceneId))
    : data;

  // Filter by search query
  const filteredData = searchQuery
    ? sceneFilteredData.filter(
        (item) =>
          item.contentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.contentType.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (item.sceneName && item.sceneName.toLowerCase().includes(searchQuery.toLowerCase())),
      )
    : sceneFilteredData;

  const sortedData = [...filteredData].sort((a, b) => {
    const multiplier = sortAsc ? 1 : -1;
    return (a[sortKey] - b[sortKey]) * multiplier;
  });

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const getSuccessRateColor = (rate: number): string => {
    if (rate >= 80) return "text-green-600";
    if (rate >= 50) return "text-yellow-600";
    return "text-red-600";
  };

  const getSuccessRateBg = (rate: number): string => {
    if (rate >= 80) return "bg-green-100";
    if (rate >= 50) return "bg-yellow-100";
    return "bg-red-100";
  };

  const getDotFillColor = (rate: number): string => {
    if (rate >= 80) return "#00c889"; // Green
    if (rate >= 50) return "#eab308"; // Yellow
    return "#ef4444"; // Red
  };

  const renderScatterTooltip = (props: ScatterTooltipProps) => {
    const { active, payload } = props;
    if (!active || !payload?.[0]) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
        <p className="font-medium text-gray-900">{d.contentName}</p>
        <p className="text-xs text-gray-500 mb-2">{d.sceneName || "-"}</p>
        <p className="text-gray-700">
          {t("statsNew.interactions.chartTooltipSuccessRate")}:{" "}
          <span className={`font-medium ${getSuccessRateColor(d.successRate)}`}>
            {d.successRate}%
          </span>
        </p>
        <p className="text-gray-700">
          {t("statsNew.interactions.chartTooltipAttempts")}:{" "}
          <span className="font-medium">{d.attemptCount}</span>
        </p>
      </div>
    );
  };

  const handleDotClick = (data: InteractionStat) => {
    if (onInteractionClick && data?.contentId) {
      onInteractionClick(data.contentId, data.contentName, data.contentType);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-500">
        {t("statsNew.charts.noData")}
      </div>
    );
  }

  // Show message if filter returns no results
  if (filterSceneId && filteredData.length === 0) {
    return (
      <div className="space-y-4">
        {/* Filter indicator */}
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="text-sm text-blue-700">
            {t("statsNew.interactions.filteredBy")}: <strong>{filterSceneName}</strong>
          </span>
          {onClearFilter && (
            <button
              onClick={onClearFilter}
              className="ml-auto text-blue-600 hover:text-blue-800 text-sm underline"
            >
              {t("statsNew.interactions.showAll")}
            </button>
          )}
        </div>
        <div className="flex items-center justify-center h-32 text-gray-500">
          {t("statsNew.interactions.noInteractionsInScene")}
        </div>
      </div>
    );
  }

  // Summary stats (use filtered data)
  const problematicCount = filteredData.filter((d) => d.successRate < 50).length;
  const avgSuccessRate =
    filteredData.length > 0
      ? Math.round(filteredData.reduce((sum, d) => sum + d.successRate, 0) / filteredData.length)
      : 0;

  return (
    <div className="space-y-4">
      {/* Filter indicator */}
      {filterSceneId && (
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 text-blue-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
          <span className="text-sm text-blue-700">
            {t("statsNew.interactions.filteredBy")}: <strong>{filterSceneName}</strong>
          </span>
          {onClearFilter && (
            <button
              onClick={onClearFilter}
              className="ml-auto text-blue-600 hover:text-blue-800 text-sm underline"
            >
              {t("statsNew.interactions.showAll")}
            </button>
          )}
        </div>
      )}

      {/* Search and Summary */}
      <div className="flex items-center gap-6 text-sm">
        {/* Search bar */}
        <div className="relative">
          <input
            type="text"
            placeholder={t("statsNew.interactions.search")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-64 pl-9 pr-8 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        <div>
          <span className="text-gray-500">{t("statsNew.interactions.totalInteractions")}: </span>
          <span className="font-medium">{filteredData.length}</span>
        </div>
        <div className="relative group flex items-center gap-1">
          <span className="text-gray-500">{t("statsNew.charts.simpleAverage")}:</span>
          <span className={`font-medium ${getSuccessRateColor(avgSuccessRate)}`}>
            {avgSuccessRate}%
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3.5 w-3.5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {/* Tooltip */}
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 w-48 text-center">
            {t("statsNew.charts.simpleAverageTooltip")}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
          </div>
        </div>
        {problematicCount > 0 && (
          <div className="flex items-center gap-1 text-red-600">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span className="font-medium">
              {problematicCount} {t("statsNew.interactions.problematic")}
            </span>
          </div>
        )}

        {/* View mode toggle */}
        <div className="ml-auto inline-flex rounded-lg border border-gray-300 overflow-hidden">
          <button
            onClick={() => setViewMode("table")}
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
              viewMode === "table"
                ? "bg-green-500 text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            {t("statsNew.interactions.tableView")}
          </button>
          <button
            onClick={() => setViewMode("chart")}
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
              viewMode === "chart"
                ? "bg-green-500 text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            {t("statsNew.interactions.chartView")}
          </button>
        </div>
      </div>

      {/* Chart View */}
      {viewMode === "chart" ? (
        <div className="h-[400px] border border-gray-200 rounded-lg p-4 bg-white relative">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                type="number"
                dataKey="successRate"
                name="Success Rate"
                domain={[0, 100]}
                tickFormatter={(v) => `${v}%`}
                label={{
                  value: t("statsNew.interactions.successRate"),
                  position: "bottom",
                  offset: 10,
                  style: { fill: "#6b7280", fontSize: 12 },
                }}
              />
              <YAxis
                type="number"
                dataKey="attemptCount"
                name="Attempts"
                label={{
                  value: t("statsNew.interactions.attempts"),
                  angle: -90,
                  position: "insideLeft",
                  style: { fill: "#6b7280", fontSize: 12 },
                }}
              />
              <ZAxis type="number" dataKey="avgDuration" range={[50, 400]} />
              <Tooltip
                content={(props) =>
                  renderScatterTooltip({ ...props, t, getSuccessRateColor } as ScatterTooltipProps)
                }
              />
              <Scatter
                data={filteredData}
                cursor="pointer"
                onClick={(data) => handleDotClick(data as unknown as InteractionStat)}
              >
                {filteredData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={getDotFillColor(entry.successRate)}
                    opacity={0.8}
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      ) : (
        /* Table View */
        <div className="overflow-auto h-[400px] rounded-lg border border-gray-200">
          <table className="min-w-full border-separate border-spacing-0">
            <thead className="sticky top-0 z-20 bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                  {t("statsNew.interactions.interaction")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                  {t("statsNew.interactions.type")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                  {t("statsNew.interactions.scene")}
                </th>
                <th
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50 border-b border-gray-200"
                  onClick={() => handleSort("attemptCount")}
                >
                  <div className="flex items-center justify-center gap-1">
                    {t("statsNew.interactions.attempts")}
                    {sortKey === "attemptCount" && <span>{sortAsc ? "↑" : "↓"}</span>}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50 border-b border-gray-200"
                  onClick={() => handleSort("successRate")}
                >
                  <div className="flex items-center justify-center gap-1">
                    {t("statsNew.interactions.avgScore")}
                    {sortKey === "successRate" && <span>{sortAsc ? "↑" : "↓"}</span>}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50 border-b border-gray-200"
                  onClick={() => handleSort("avgDuration")}
                >
                  <div className="flex items-center justify-center gap-1">
                    {t("statsNew.interactions.avgTime")}
                    {sortKey === "avgDuration" && <span>{sortAsc ? "↑" : "↓"}</span>}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {sortedData.length === 0 && searchQuery ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                    {t("general.noResults")}
                  </td>
                </tr>
              ) : (
                sortedData.map((item) => (
                  <tr
                    key={item.contentId}
                    className={`${
                      item.successRate < 50 ? "bg-red-50 hover:bg-red-100" : "hover:bg-gray-50"
                    } ${onInteractionClick ? "cursor-pointer" : ""} transition-colors`}
                    onClick={() =>
                      onInteractionClick?.(item.contentId, item.contentName, item.contentType)
                    }
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 border-b border-gray-200">
                      {item.contentName}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 border-b border-gray-200">
                      {formatContentTypeName(item.contentType)}
                    </td>
                    <td className="px-4 py-3 text-sm border-b border-gray-200">
                      {item.sceneId && item.sceneName && onSceneClick ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (item.sceneId && item.sceneName) {
                              onSceneClick(item.sceneId, item.sceneName);
                            }
                          }}
                          className="group flex items-center gap-1 text-gray-700 hover:text-green-600 text-left transition-colors"
                        >
                          <span>{item.sceneName}</span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3.5 w-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                            />
                          </svg>
                        </button>
                      ) : (
                        <span className="text-gray-500">{item.sceneName || "-"}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 text-center border-b border-gray-200">
                      {item.attemptCount}
                    </td>
                    <td className="px-4 py-3 text-center border-b border-gray-200">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSuccessRateBg(
                          item.successRate,
                        )} ${getSuccessRateColor(item.successRate)}`}
                      >
                        {item.successRate}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 text-center border-b border-gray-200">
                      {formatDuration(item.avgDuration)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InteractionAnalysisTable;
